import React from 'react';
import { EmptyState } from '../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { Separator } from '../../../CommonComponents/OPL/Separator/Separator';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { formatDate } from '../../../CommonUtils/formatDate';
import { usePersonalFinanceTemplatesQuery } from './api/usePersonalFinanceTemplatesQuery';
import { DeleteModal } from './components/DeleteModal/DeleteModal';
import { Header } from './components/Header/Header';
import { TemplatesLoading } from './Templates.loading';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';

export const Templates = () => {
  const [templateSelected, setTemplateSelected] = React.useState<{
    name: string;
    id: string;
  } | null>(null);

  const { data, isFetching, error, refetch } = usePersonalFinanceTemplatesQuery();

  if (isFetching) {
    return <TemplatesLoading />;
  }

  if (error) {
    return (
      <Box height="100%" width="100%" display="flex" flexDirection="column" justifyContent="center">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="8">
        <Header />

        <Button
          onClick={() => {
            ADV.util.Router.go2('PersonalFinanceTemplate');
          }}
        >
          Create New Template
        </Button>
      </Box>

      <Box my="24">
        <Separator />
      </Box>

      <HtmlTable variant="bordered" columnSpacing="small">
        <table>
          <colgroup>
            <col style={{ width: '50%' }} />
            <col style={{ width: '45%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <Text variant="tableHeader" textAlign="left">
                  Template Name
                </Text>
              </th>
              <th>
                <Text variant="tableHeader" textAlign="left">
                  Last Updated
                </Text>
              </th>

              <th>&nbsp;</th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan={2}>
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <EmptyState message="No Templates to display." />
                  </Box>
                </td>
              </tr>
            )}

            {data.map(template => (
              <tr key={template.templateId}>
                <td>
                  <Box
                    display="flex"
                    flexDirection="column"
                    css={({ theme }) => ({ gap: theme.space['2'] })}
                  >
                    <Text fontWeight={500}>{template.name}</Text>
                    {template.isReadOnly && (
                      <HelperText>Default templates cannot be modified</HelperText>
                    )}
                  </Box>
                </td>

                <td>{formatDate(template.lastUpdated)}</td>

                <td>
                  {template.isReadOnly ? (
                    <Box display="flex" width="100%" justifyContent="space-between">
                      <IconButton
                        icon="fas fa-eye"
                        variant="subtleInactive"
                        onClick={() => {
                          ADV.util.Router.go2('PersonalFinanceTemplate', {
                            templateId: template.templateId,
                          });
                        }}
                        aria-label={`View ${template.name}`}
                      />
                    </Box>
                  ) : (
                    <Box display="flex" width="100%" justifyContent="space-between">
                      <IconButton
                        icon="fas fa-pencil-alt"
                        variant="subtleInactive"
                        onClick={() => {
                          ADV.util.Router.go2('PersonalFinanceTemplate', {
                            templateId: template.templateId,
                          });
                        }}
                        aria-label={`Edit ${template.name}`}
                      />

                      <IconButton
                        icon="far fa-trash-alt"
                        variant="subtleInactive"
                        onClick={() => {
                          setTemplateSelected({ name: template.name, id: template.templateId });
                        }}
                        aria-label={`Delete ${template.name}`}
                      />
                    </Box>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HtmlTable>

      {templateSelected && (
        <DeleteModal
          id={templateSelected.id}
          name={templateSelected.name}
          onClose={() => {
            setTemplateSelected(null);
          }}
        />
      )}
    </Box>
  );
};
